import React, { useState } from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ApiService } from "services";
import { Router } from "utils/route";
import DashboardLayout from "layouts/DashboardLayout";
import { AiOutlineArrowLeft } from "react-icons/ai";

export default function CreditHistory() {
  const [param] = useSearchParams();
  const code = param.get("code");

  const { route } = Router();

  const [creditHistoryStartDate, setCreditHistoryStartDate] = useState("");
  const [creditHistoryEndDate, setCreditHistoryEndDate] = useState("");
  const [creditHistoryLoading, setCreditHistoryLoading] = useState(false);
  const [creditHistoryResponse, setCreditHistoryResponse] = useState(null);
  const [creditHistoryPage, setCreditHistoryPage] = useState(1);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const postingsData = creditHistoryResponse?.postings || creditHistoryResponse?.transactions;

  const fetchCreditHistory = async (pageNumber) => {
    setCreditHistoryLoading(true);
    setCreditHistoryResponse(null);

    try {
      const response = await ApiService.getCreditHistory({
        institutionCode: code,
        dateFrom: creditHistoryStartDate,
        dateTo: creditHistoryEndDate,
        page: pageNumber,
      });
      console.log(response, 'response')
      if (response && response.status && response.data) {
        setCreditHistoryResponse(response.data);
      } else if (response && response.data) {
        setCreditHistoryResponse(response.data);
      } else {
        setCreditHistoryResponse(null);
        toast.error("Failed to find credit history data in response.");
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to fetch credit history.");
    } finally {
      setCreditHistoryLoading(false);
    }
  };

  const handleCreditHistorySubmit = async (e) => {
    e.preventDefault();

    if (!creditHistoryStartDate || !creditHistoryEndDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    const start = new Date(creditHistoryStartDate);
    const end = new Date(creditHistoryEndDate);

    if (end < start) {
      toast.error("End date cannot be before start date.");
      return;
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 10) {
      toast.error("Date range cannot exceed 10 days.");
      return;
    }

    setCreditHistoryPage(1);
    fetchCreditHistory(1);
  };

  const handleDownloadToExcel = async () => {
    setDownloadLoading(true);
    const toastId = toast.loading("Downloading credit history...");
    try {
      const responseData = await ApiService.getCreditHistory({
        institutionCode: code,
        dateFrom: creditHistoryStartDate,
        dateTo: creditHistoryEndDate,
        download: true,
      });
      console.log(responseData, 'response')

      const blob = responseData instanceof Blob
        ? responseData
        : new Blob([responseData], { type: "text/csv;charset=utf-8;" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `credit_history_${code || "institution"}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded successfully!", { id: toastId });
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to download credit history.", { id: toastId });
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Container>
        <div style={{ marginBottom: "20px" }}>
          <p
            onClick={() => route(-1)}
            style={{
              color: "#14A2FD",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            <AiOutlineArrowLeft size={20} style={{ marginRight: "10px" }} />{" "}
            Back to Institution Details
          </p>
        </div>

        <div className="first_section">
          <div className="title_container">
            <h2>Credit History</h2>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #E8E8E8",
            borderRadius: "8px",
            overflow: "hidden",
            background: "#F9F9F9",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              background: "#14a2fd",
              color: "white",
              padding: "10px 16px",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            Credit History Request (Max of 10days range per request)
          </div>
          <form
            onSubmit={handleCreditHistorySubmit}
            style={{
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "150px 1fr",
                border: "1px solid #ddd",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#f5f5f5",
                  padding: "10px",
                  borderRight: "1px solid #ddd",
                  borderBottom: "1px solid #ddd",
                  fontWeight: "600",
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Start Date
              </div>
              <div
                style={{
                  padding: "4px 8px",
                  borderBottom: "1px solid #ddd",
                  background: "white",
                }}
              >
                <input
                  type="date"
                  value={creditHistoryStartDate}
                  onChange={(e) => setCreditHistoryStartDate(e.target.value)}
                  required
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    height: "32px",
                    fontSize: "14px",
                    color: "#333",
                  }}
                />
              </div>

              <div
                style={{
                  background: "#f5f5f5",
                  padding: "10px",
                  borderRight: "1px solid #ddd",
                  fontWeight: "600",
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                End Date
              </div>
              <div
                style={{
                  padding: "4px 8px",
                  background: "white",
                }}
              >
                <input
                  type="date"
                  value={creditHistoryEndDate}
                  onChange={(e) => setCreditHistoryEndDate(e.target.value)}
                  required
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    height: "32px",
                    fontSize: "14px",
                    color: "#333",
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={creditHistoryLoading}
              style={{
                background: "#14a2fd",
                color: "white",
                border: "none",
                borderRadius: "4px",
                padding: "8px 24px",
                fontWeight: "600",
                cursor: creditHistoryLoading ? "not-allowed" : "pointer",
                opacity: creditHistoryLoading ? 0.7 : 1,
                width: "max-content",
                alignSelf: "flex-start",
              }}
            >
              {creditHistoryLoading ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>

        {creditHistoryLoading && (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#666", fontSize: "16px", fontWeight: "600" }}>
            Loading credit history details...
          </div>
        )}

        {!creditHistoryLoading && creditHistoryResponse && (
          <div style={{ marginTop: "24px" }}>
            <div
              style={{
                border: "1px solid #ddd",
                background: "white",
                padding: "16px",
                borderRadius: "0 4px 0 0",
                borderBottom: "none",
              }}
            >
              {(creditHistoryResponse.organisation || creditHistoryResponse.account_number) && (
                <h5 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#333", fontWeight: "600" }}>
                  {creditHistoryResponse.organisation && (
                    <>
                      Organisation: {creditHistoryResponse.organisation.name} ({creditHistoryResponse.organisation.institution_type}) — Code: {creditHistoryResponse.organisation.code}
                    </>
                  )}
                  {creditHistoryResponse.account_number && (
                    <>
                      {creditHistoryResponse.organisation ? " — " : ""}Account Number: {creditHistoryResponse.account_number}
                    </>
                  )}
                </h5>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div
                  style={{
                    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                    borderRadius: "8px",
                    padding: "16px",
                    color: "white",
                  }}
                >
                  <div style={{ fontSize: "13px", opacity: 0.9, marginBottom: "4px" }}>Total Credit Amount</div>
                  <div style={{ fontSize: "20px", fontWeight: "700" }}>
                    ₦{creditHistoryResponse.credit_amount !== undefined && creditHistoryResponse.credit_amount !== null
                      ? parseFloat(creditHistoryResponse.credit_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })
                      : "0.00"}
                  </div>
                </div>
                <div
                  style={{
                    background: "linear-gradient(135deg, #3a7bd5 0%, #3a6073 100%)",
                    borderRadius: "8px",
                    padding: "16px",
                    color: "white",
                  }}
                >
                  <div style={{ fontSize: "13px", opacity: 0.9, marginBottom: "4px" }}>Total Credit Count</div>
                  <div style={{ fontSize: "20px", fontWeight: "700" }}>
                    {creditHistoryResponse.credit_count !== undefined && creditHistoryResponse.credit_count !== null
                      ? creditHistoryResponse.credit_count
                      : "0"}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "0 0 4px 4px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", minWidth: "800px" }}>
                <thead>
                  <tr style={{ background: "#d1d7dc", color: "#333" }}>
                    <th style={{ padding: "10px 8px", textAlign: "left", borderBottom: "1px solid #c4cbd0", borderRight: "1px solid #c4cbd0", fontWeight: "600" }}>ID</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", borderBottom: "1px solid #c4cbd0", borderRight: "1px solid #c4cbd0", fontWeight: "600" }}>Request ID</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", borderBottom: "1px solid #c4cbd0", borderRight: "1px solid #c4cbd0", fontWeight: "600" }}>Account</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", borderBottom: "1px solid #c4cbd0", borderRight: "1px solid #c4cbd0", fontWeight: "600" }}>Date</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", borderBottom: "1px solid #c4cbd0", borderRight: "1px solid #c4cbd0", fontWeight: "600" }}>Time</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", borderBottom: "1px solid #c4cbd0", borderRight: "1px solid #c4cbd0", fontWeight: "600" }}>Amount</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", borderBottom: "1px solid #c4cbd0", fontWeight: "600" }}>Narration</th>
                  </tr>
                </thead>
                <tbody>
                  {!postingsData?.results || postingsData.results.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                        No credit history found for the selected dates.
                      </td>
                    </tr>
                  ) : (
                    postingsData.results.map((item, index) => {
                      let dateStr = "";
                      let timeStr = "";
                      if (item.occured) {
                        const parts = item.occured.split("T");
                        dateStr = parts[0] || "";
                        timeStr = parts[1] || "";
                      }
                      return (
                        <tr key={item.id || index} style={{ borderBottom: "1px solid #ddd", background: index % 2 === 0 ? "#ffffff" : "#f9f9f9" }}>
                          <td style={{ padding: "8px", borderRight: "1px solid #ddd" }}>{item.id || "-"}</td>
                          <td style={{ padding: "8px", borderRight: "1px solid #ddd" }}>{item.requestId || "-"}</td>
                          <td style={{ padding: "8px", borderRight: "1px solid #ddd" }}>{item.account || "-"}</td>
                          <td style={{ padding: "8px", borderRight: "1px solid #ddd" }}>{dateStr}</td>
                          <td style={{ padding: "8px", borderRight: "1px solid #ddd" }}>{timeStr}</td>
                          <td style={{ padding: "8px", borderRight: "1px solid #ddd", fontWeight: "600" }}>
                            {item.amount !== undefined && item.amount !== null ? `₦${parseFloat(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-"}
                          </td>
                          <td style={{ padding: "8px" }}>{item.narration || "-"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {postingsData && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
                <div style={{ fontSize: "13px", color: "#666" }}>
                  Showing Page {creditHistoryPage} (Total Transactions: {postingsData.count || 0})
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    type="button"
                    disabled={!postingsData.previous || creditHistoryLoading}
                    onClick={() => {
                      const prevPage = creditHistoryPage - 1;
                      setCreditHistoryPage(prevPage);
                      fetchCreditHistory(prevPage);
                    }}
                    style={{
                      background: postingsData.previous ? "#14a2fd" : "#e0e0e0",
                      color: postingsData.previous ? "white" : "#a0a0a0",
                      border: "none",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: postingsData.previous ? "pointer" : "not-allowed",
                      transition: "background 0.2s",
                    }}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={!postingsData.next || creditHistoryLoading}
                    onClick={() => {
                      const nextPage = creditHistoryPage + 1;
                      setCreditHistoryPage(nextPage);
                      fetchCreditHistory(nextPage);
                    }}
                    style={{
                      background: postingsData.next ? "#14a2fd" : "#e0e0e0",
                      color: postingsData.next ? "white" : "#a0a0a0",
                      border: "none",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: postingsData.next ? "pointer" : "not-allowed",
                      transition: "background 0.2s",
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {postingsData?.results?.length > 0 && (
              <button
                onClick={handleDownloadToExcel}
                disabled={downloadLoading}
                style={{
                  marginTop: "12px",
                  background: "#14a2fd",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  fontWeight: "600",
                  cursor: downloadLoading ? "not-allowed" : "pointer",
                  opacity: downloadLoading ? 0.7 : 1,
                  fontSize: "13px",
                }}
              >
                {downloadLoading ? "Downloading..." : "Download to Excel"}
              </button>
            )}
          </div>
        )}
      </Container>
    </DashboardLayout>
  );
}

const Container = styled.div`
  padding: 30px;
  background-color: #eff7fa;
  min-height: 100vh;
  .first_section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1.5px solid gainsboro;
    padding-bottom: 20px;
    margin-bottom: 20px;
  }
  .title_container {
    h2 {
      font-size: 26px;
      font-weight: 600;
      color: black;
      text-transform: capitalize;
    }
  }
`;
